import { useEffect, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { PageFrame } from '../components/work/page-chrome';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useOverlays } from '../components/work/overlay-context';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageTitle } from '../components/work/page-states';
import { formatTimestampDate } from '../lib/dates';

const SYSTEM_ROLES = ['admin', 'user'] as const;
type SystemRole = (typeof SYSTEM_ROLES)[number];

type AdminUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  isActive: boolean;
  createdAt: string;
  roles: string[];
  whatsappPhone: string | null;
};

function isSystemRole(value: string): value is SystemRole {
  return value === 'admin' || value === 'user';
}

function isAdminUser(value: unknown): value is AdminUser {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  return (
    typeof rec.id === 'string' &&
    typeof rec.email === 'string' &&
    typeof rec.isActive === 'boolean' &&
    typeof rec.createdAt === 'string' &&
    Array.isArray(rec.roles) &&
    rec.roles.every((role) => typeof role === 'string') &&
    (rec.whatsappPhone === undefined || rec.whatsappPhone === null || typeof rec.whatsappPhone === 'string')
  );
}

function parseUserList(data: unknown): { items: AdminUser[]; total: number } | null {
  if (!data || typeof data !== 'object') return null;
  const rec = data as Record<string, unknown>;
  if (!Array.isArray(rec.items) || typeof rec.total !== 'number') return null;
  if (!rec.items.every(isAdminUser)) return null;
  return {
    items: rec.items.map((item) => ({
      ...item,
      whatsappPhone: item.whatsappPhone ?? null,
    })),
    total: rec.total,
  };
}

function apiErrorText(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const rec = data as Record<string, unknown>;
  return typeof rec.error === 'string' && rec.error ? rec.error : fallback;
}

function formatCreated(value: unknown): string {
  if (typeof value !== 'string' || !value) return '-';
  return formatTimestampDate(value) ?? '-';
}

function isSystemAdmin(user: AdminUser): boolean {
  return user.roles.includes('admin');
}

function emptyForm() {
  return { email: '', firstName: '', lastName: '', role: 'user' as SystemRole };
}

export default function AdminUsers() {
  const [items, setItems] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);
  const [whatsappUser, setWhatsappUser] = useState<AdminUser | null>(null);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const { toast, confirm } = useOverlays();

  async function load() {
    const res = await fetch(`/api/admin/users?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`);
    if (!res.ok) {
      setItems([]);
      setTotal(0);
      toast(res.status === 403 ? 'You cannot manage users.' : 'Could not load users.');
      return;
    }
    const parsed = parseUserList(await res.json());
    if (!parsed) {
      setItems([]);
      setTotal(0);
      toast('Could not load users.');
      return;
    }
    setItems(parsed.items);
    setTotal(parsed.total);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, page, pageSize]);

  async function createUser() {
    if (creating) return;
    const email = form.email.trim();
    if (!email) {
      toast('Email is required.');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: form.firstName,
          lastName: form.lastName,
          roles: [form.role],
        }),
      });
      if (!res.ok) {
        toast(res.status === 409 ? 'That email already has an account.' : 'Could not create user.');
        return;
      }
      setOpen(false);
      setForm(emptyForm());
      toast('User created. They will get a link to set their own password.');
      await load();
    } catch {
      toast('Could not create user.');
    } finally {
      setCreating(false);
    }
  }

  async function changeRole(user: AdminUser, role: SystemRole) {
    if (!user.id) {
      toast('User id is missing');
      return;
    }
    if (user.roles.length === 1 && user.roles[0] === role) return;
    setActingUserId(user.id);
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles: [role] }),
      });
      if (!res.ok) {
        toast(apiErrorText(await res.json().catch(() => null), 'Failed to update role'));
        return;
      }
      toast(role === 'admin' ? 'Member is now a system admin' : 'Member role updated');
      await load();
    } catch {
      toast('Failed to update role');
    } finally {
      setActingUserId(null);
    }
  }

  async function toggleActive(user: AdminUser) {
    if (!user.id) {
      toast('User id is missing');
      return;
    }
    const res = await fetch(`/api/admin/users/${user.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive: !user.isActive }),
    });
    if (!res.ok) {
      toast(user.isActive ? 'Could not deactivate user' : 'Could not activate user');
      return;
    }
    await load();
  }

  async function removeUser(user: AdminUser) {
    if (!user.id) {
      toast('User id is missing');
      return;
    }
    const ok = await confirm({
      title: 'Delete user',
      description: `Delete ${user.email}? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    const res = await fetch(`/api/admin/users/${user.id}`, { method: 'DELETE' });
    if (!res.ok) {
      toast('Could not delete user');
      return;
    }
    toast('User deleted');
    await load();
  }

  function openWhatsapp(user: AdminUser) {
    setWhatsappUser(user);
    setWhatsappPhone(user.whatsappPhone ?? '');
  }

  async function saveWhatsapp() {
    if (!whatsappUser) return;
    setSavingWhatsapp(true);
    try {
      const res = await fetch(`/api/admin/users/${whatsappUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsappPhone: whatsappPhone.trim() === '' ? null : whatsappPhone.trim() }),
      });
      if (!res.ok) {
        toast(apiErrorText(await res.json().catch(() => null), 'Could not save WhatsApp number'));
        return;
      }
      toast(whatsappPhone.trim() ? 'WhatsApp number saved. They can summon Pulse.' : 'Removed from the WhatsApp allowlist.');
      setWhatsappUser(null);
      await load();
    } catch {
      toast('Could not save WhatsApp number');
    } finally {
      setSavingWhatsapp(false);
    }
  }

  async function resetPassword(user: AdminUser) {
    if (!user.id) {
      toast('User id is missing');
      return;
    }
    const res = await fetch(`/api/admin/users/${user.id}/reset`, { method: 'POST' });
    if (!res.ok) {
      toast('Could not send reset email');
      return;
    }
    toast('Reset sent if SMTP is configured. Otherwise the link was logged on the server.');
  }

  return (
    <PageFrame>
      <div className="space-y-4">
        <PageTitle
          title="Users"
          count={total}
          action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm">New User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create User</DialogTitle>
                <DialogDescription>
                  They will receive a link to set their own password. You never choose or see it.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="role">System role</Label>
                  <Select
                    value={form.role}
                    onValueChange={(value) => {
                      if (isSystemRole(value)) setForm({ ...form, role: value });
                    }}
                  >
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">System admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createUser} disabled={creating}>
                  {creating ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          }
        />
        <ListFilterBar
          query={q.trim() || null}
          onQueryChange={(query) => {
            setPage(1);
            setQ(query ?? '');
          }}
          searchPlaceholder="Search email..."
          searchAriaLabel="Search users"
          onClear={() => {
            setPage(1);
            setQ('');
          }}
        />
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0 w-[24%]">Email</TableHead>
              <TableHead className="min-w-0 w-[16%]">Name</TableHead>
              <TableHead className="w-32">Role</TableHead>
              <TableHead className="min-w-0 w-[18%]">WhatsApp</TableHead>
              <TableHead className="w-20">Active</TableHead>
              <TableHead className="w-32">Created</TableHead>
              <TableHead className="w-12 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u) => {
              const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
              const created = formatCreated(u.createdAt);
              return (
                <TableRow key={u.id}>
                  <TableCell className="min-w-0 truncate text-sm font-medium">{u.email}</TableCell>
                  <TableCell className="min-w-0 truncate text-sm font-medium">{name || '-'}</TableCell>
                  <TableCell>
                    {isSystemAdmin(u) ? (
                      <Badge>System admin</Badge>
                    ) : (
                      <Badge variant="secondary">User</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.whatsappPhone && u.isActive ? 'Allowed' : 'No'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                    {created}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={actingUserId === u.id}
                          aria-label={`Actions for ${u.email}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!isSystemAdmin(u) ? (
                          <DropdownMenuItem onSelect={() => void changeRole(u, 'admin')}>
                            Make system admin
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => void changeRole(u, 'user')}>
                            Make user
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onSelect={() => openWhatsapp(u)}>
                          {u.whatsappPhone ? 'Edit WhatsApp number' : 'Add to WhatsApp allowlist'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void toggleActive(u)}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void resetPassword(u)}>
                          Reset password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void removeUser(u)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <p className="font-mono tabular-nums">
            Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= Math.max(1, Math.ceil(total / pageSize))}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number.parseInt(e.target.value, 10);
                if (!Number.isInteger(next) || next <= 0) return;
                setPageSize(next);
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              {[10, 20, 50].map((s) => (
                <option key={s} value={s}>
                  {s}/page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <Dialog open={whatsappUser !== null} onOpenChange={(open) => { if (!open) setWhatsappUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {whatsappUser?.whatsappPhone ? 'Edit WhatsApp number' : 'Add to WhatsApp allowlist'}
            </DialogTitle>
            <DialogDescription>
              Active users with a number can summon Pulse in WhatsApp groups. Leave blank to remove them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <p className="truncate text-sm text-muted-foreground">{whatsappUser?.email}</p>
            <div>
              <Label htmlFor="whatsappPhone">WhatsApp number</Label>
              <Input
                id="whatsappPhone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+447700900123"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
            </div>
            <Button onClick={() => void saveWhatsapp()} disabled={savingWhatsapp}>
              {savingWhatsapp ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageFrame>
  );
}
